import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import VideoCard from '../components/VideoCard';
import { useVideo } from '../context/VideoContext';

const ShortsPage = () => {
  const { videos, visibleVideos, loadVisibleVideos, totalCount, loading, loadError } = useVideo();
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const containerRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Update visible videos when current index changes
  useEffect(() => {
    // Only load visible videos if we have videos to show
    if (videos.length > 0) {
      loadVisibleVideos(currentVideoIndex);
    }
  }, [currentVideoIndex, loadVisibleVideos, videos.length]);
  
  // New effect to trigger initial video playback
  useEffect(() => {
    if (!loading && visibleVideos.length > 0 && !initialLoadComplete) {
      // Force a re-render by toggling isTransitioning
      setIsTransitioning(true);
      
      // Short timeout to ensure DOM is ready
      setTimeout(() => {
        setIsTransitioning(false);
        setInitialLoadComplete(true);
      }, 100);
    }
  }, [loading, visibleVideos, initialLoadComplete]);

  const scrollToVideo = (index) => {
    if (index < 0 || index >= totalCount || isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentVideoIndex(index);
    
    // Short timeout to allow transition to complete
    setTimeout(() => {
      setIsTransitioning(false);
    }, 400);
  };

  const goToNextVideo = useCallback(() => {
    if (currentVideoIndex < totalCount - 1 && !isTransitioning) {
      scrollToVideo(currentVideoIndex + 1);
    }
  }, [currentVideoIndex, totalCount, isTransitioning]);

  const goToPreviousVideo = useCallback(() => {
    if (currentVideoIndex > 0 && !isTransitioning) {
      scrollToVideo(currentVideoIndex - 1);
    }
  }, [currentVideoIndex, isTransitioning]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowUp') {
        goToPreviousVideo();
      } else if (e.key === 'ArrowDown') {
        goToNextVideo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNextVideo, goToPreviousVideo]);

  // Improved wheel scrolling with debounce
  const lastWheelTime = useRef(0);
  const handleWheel = useCallback((e) => {
    // Only prevent default if we're actually going to handle this scroll
    const now = Date.now();
    // Reduce debounce time from 800ms to 400ms for better responsiveness
    if (now - lastWheelTime.current < 1000) return;
    
    // Handle the scroll only for significant movements
    if (!isTransitioning) {
      if (e.deltaY > 15) {
        e.preventDefault(); // Only prevent default when we handle the scroll
        lastWheelTime.current = now;
        goToNextVideo();
      } else if (e.deltaY < -15) {
        e.preventDefault(); // Only prevent default when we handle the scroll
        lastWheelTime.current = now;
        goToPreviousVideo();
      }
    }
  }, [goToNextVideo, goToPreviousVideo, isTransitioning]);

  // Touch navigation
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };
  
  const handleTouchMove = (e) => {
    // Prevent default scrolling when we detect a vertical swipe
    if (touchStart) {
      const currentY = e.targetTouches[0].clientY;
      const diff = touchStart - currentY;
      
      // If it seems like a deliberate vertical swipe, prevent default
      if (Math.abs(diff) > 50) {
        e.preventDefault();
      }
    }
    setTouchEnd(e.targetTouches[0].clientY);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isSignificantSwipe = Math.abs(distance) > 100;
    
    if (isSignificantSwipe) {
      if (distance > 0) {
        goToNextVideo();
      } else {
        goToPreviousVideo();
      }
    }
    
    // Reset values
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <div 
      ref={containerRef}
      className="flex flex-col items-center justify-center min-h-screen bg-black overflow-hidden pt-6 pb-16"
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative w-full max-w-md h-[calc(100vh-96px)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-white text-center">
              <div className="h-12 w-12 rounded-full border-4 border-gray-300 border-t-pink-500 animate-spin mx-auto mb-4"></div>
              <p className="text-xl font-light">Loading videos...</p>
            </div>
          </div>
        ) : loadError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-white text-center p-6 bg-black/80 backdrop-blur-md rounded-2xl max-w-xs">
              <p className="text-red-400 text-2xl mb-4 font-medium">Couldn't Load Videos</p>
              <p className="text-gray-300 mb-6">{loadError}</p>
              <div className="p-4 bg-gray-900/60 rounded-xl text-left">
                <p className="text-sm mb-2 text-gray-400">Try these steps:</p>
                <ol className="list-decimal list-inside text-sm space-y-2 text-gray-300">
                  <li>Check videos in /public/videos/</li>
                  <li>Verify file paths in metadata</li>
                  <li>Refresh the page</li>
                </ol>
              </div>
            </div>
          </div>
        ) : totalCount > 0 ? (
          <div 
            className="h-full relative transition-transform duration-400 ease-out will-change-transform"
            style={{ transform: `translateY(-${currentVideoIndex * 100}%)` }}
          >
            {visibleVideos.map((video, idx) => {
              // Get the actual index in the full video list
              const actualIndex = videos.findIndex(v => v.id === video.id);
              
              return (
                <div 
                  key={video.id}
                  className="h-full w-full absolute top-0 left-0 overflow-hidden"
                  style={{ transform: `translateY(${actualIndex * 100}%)` }}
                >
                  <VideoCard 
                    video={video} 
                    isActive={actualIndex === currentVideoIndex && !isTransitioning} 
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-white text-xl font-light">No videos available</p>
          </div>
        )}
        
        {totalCount > 0 && (
          <>
            <button 
              className={`absolute top-6 left-1/2 transform -translate-x-1/2 z-30 p-2 rounded-full 
                bg-black/20 backdrop-blur-md transition-all duration-200
                ${currentVideoIndex === 0 || isTransitioning 
                  ? 'opacity-0 pointer-events-none' 
                  : 'opacity-80 hover:opacity-100 hover:bg-black/40'}`}
              onClick={goToPreviousVideo}
              disabled={currentVideoIndex === 0 || isTransitioning}
            >
              <ChevronUpIcon className="h-5 w-5 text-white" />
            </button>
            
            <button 
              className={`absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30 p-2 rounded-full 
                bg-black/20 backdrop-blur-md transition-all duration-200
                ${currentVideoIndex === totalCount - 1 || isTransitioning 
                  ? 'opacity-0 pointer-events-none' 
                  : 'opacity-80 hover:opacity-100 hover:bg-black/40'}`}
              onClick={goToNextVideo}
              disabled={currentVideoIndex === totalCount - 1 || isTransitioning}
            >
              <ChevronDownIcon className="h-5 w-5 text-white" />
            </button>
            
            <div className="absolute right-3 top-1/2 -translate-y-1/2 z-30">
              <div className="flex flex-col space-y-1.5">
                {totalCount > 10 ? (
                  // Progress indicator for large collections
                  <div className="h-32 w-1.5 bg-white/20 rounded-full relative">
                    <div 
                      className="absolute w-1.5 bg-gradient-to-b from-pink-500 to-purple-500 rounded-full"
                      style={{
                        top: 0,
                        height: `${(currentVideoIndex / (totalCount - 1)) * 100}%`
                      }}
                    />
                  </div>
                ) : (
                  // Dot indicators for smaller collections
                  Array.from({ length: totalCount }).map((_, index) => (
                    <div 
                      key={index}
                      className={`rounded-full transition-all duration-300 ${
                        index === currentVideoIndex 
                          ? 'h-5 w-1.5 bg-gradient-to-b from-pink-500 to-purple-500' 
                          : 'h-1.5 w-1.5 bg-white/40'
                      }`}
                    />
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShortsPage;